import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

const PCMAnalysisChart = ({ assessment, language }) => {
  if (!assessment || !assessment.scores) {
    return null;
  }

  // PCM Data définitions
  const PCM_TYPES = {
    analytical: {
      name: { fr: 'Manager Analytique', en: 'Analytical Manager' },
      stressPhase: { fr: 'Perfectionnisme, Critique', en: 'Perfectionism, Criticism' },
      channel: { fr: 'Réflexion logique', en: 'Logical thinking' }
    },
    principled: {
      name: { fr: 'Manager de Conviction', en: 'Persister Manager' },
      stressPhase: { fr: 'Rigidité, Moralisation', en: 'Rigidity, Moralizing' },
      channel: { fr: 'Directives claires', en: 'Clear directives' }
    },
    collaborative: {
      name: { fr: 'Manager Collaboratif', en: 'Harmonizer Manager' },
      stressPhase: { fr: 'Surprotection, Martyrisation', en: 'Overprotection, Martyrdom' },
      channel: { fr: 'Bienveillance émotionnelle', en: 'Emotional care' }
    },
    creative: {
      name: { fr: 'Manager Innovant', en: 'Energizer Manager' },
      stressPhase: { fr: 'Dispersion, Confusion', en: 'Scatter, Confusion' },
      channel: { fr: 'Stimulation ludique', en: 'Playful stimulation' }
    },
    visionary: {
      name: { fr: 'Manager Stratégique', en: 'Imaginer Manager' },
      stressPhase: { fr: 'Isolement, Imaginaire', en: 'Isolation, Fantasy' },
      channel: { fr: 'Vision inspirante', en: 'Inspiring vision' }
    },
    dynamic: {
      name: { fr: 'Manager d\'Action', en: 'Promoter Manager' },
      stressPhase: { fr: 'Manipulation, Charme', en: 'Manipulation, Charm' },
      channel: { fr: 'Défi et action', en: 'Challenge and action' }
    }
  };

  // Couleurs PCM selon vos spécifications
  const colors = {
    analytical: '#007BFF',    // Manager Analytique : bleu
    principled: '#8E44AD',    // Manager de Conviction : violet  
    collaborative: '#FF8C00', // Manager Collaboratif : orange
    creative: '#FFD700',      // Manager Innovant : jaune
    visionary: '#8B4513',     // Manager Stratégique : marron
    dynamic: '#DC143C'        // Manager d'Action : rouge
  };

  // Préparer les données pour le graphique en barres
  const chartData = Object.entries(assessment.scores).map(([type, score]) => ({
    type: PCM_TYPES[type]?.name[language] || type,
    score: score,
    typeKey: type,
    percentage: Math.round((score / 30) * 100),
    maxScore: 30
  })).sort((a, b) => b.score - a.score);

  // Identifier les phases de stress
  const getStressAnalysis = () => {
    const dominantType = assessment.dominant_type;
    const secondaryType = Object.entries(assessment.scores)
      .sort(([,a], [,b]) => b - a)[1]?.[0];
    
    return {
      dominant: {
        type: dominantType,
        data: PCM_TYPES[dominantType],
        score: assessment.scores[dominantType]
      },
      secondary: secondaryType ? {
        type: secondaryType,
        data: PCM_TYPES[secondaryType],
        score: assessment.scores[secondaryType]
      } : null
    };
  };

  const stressAnalysis = getStressAnalysis();

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800">{label}</p>
          <p className="text-blue-600">
            {language === 'fr' ? 'Score' : 'Score'}: {data.score}/30 ({data.percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Graphique en barres principal */}
      <div className="bg-white p-6 rounded-lg border">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          {language === 'fr' ? 'Profil PCM - Scores Détaillés' : 'PCM Profile - Detailed Scores'}
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="type" 
              angle={-45}
              textAnchor="end"
              height={100}
              fontSize={12}
            />
            <YAxis domain={[0, 30]} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="score" radius={[4, 4, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[entry.typeKey]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Analyse des phases de stress */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Profil dominant */}
        <div className="bg-white p-6 rounded-lg border">
          <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center gap-2">
            🎯 {language === 'fr' ? 'Profil Dominant' : 'Dominant Profile'}
          </h4>
          {stressAnalysis.dominant?.data && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: colors[stressAnalysis.dominant.type] }}
                ></div>
                <span className="font-medium">
                  {stressAnalysis.dominant.data.name[language]}
                </span>
                <span className="text-gray-600">
                  ({stressAnalysis.dominant.score}/30)
                </span>
              </div>
              
              <div className="bg-red-50 p-3 rounded border-l-4 border-red-400">
                <p className="text-sm font-medium text-red-800 mb-1">
                  ⚠️ {language === 'fr' ? 'Phase de Stress' : 'Stress Phase'}
                </p>
                <p className="text-sm text-red-700">
                  {stressAnalysis.dominant.data.stressPhase[language]}
                </p>
              </div>
              
              <div className="bg-blue-50 p-3 rounded border-l-4 border-blue-400">
                <p className="text-sm font-medium text-blue-800 mb-1">
                  💬 {language === 'fr' ? 'Canal de Communication' : 'Communication Channel'}
                </p>
                <p className="text-sm text-blue-700">
                  {stressAnalysis.dominant.data.channel[language]}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Profil secondaire */}
        {stressAnalysis.secondary && (
          <div className="bg-white p-6 rounded-lg border">
            <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center gap-2">
              🥈 {language === 'fr' ? 'Profil Secondaire' : 'Secondary Profile'}
            </h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: colors[stressAnalysis.secondary.type] }}
                ></div>
                <span className="font-medium">
                  {stressAnalysis.secondary.data.name[language]}
                </span>
                <span className="text-gray-600">
                  ({stressAnalysis.secondary.score}/30)
                </span>
              </div>
              
              <div className="bg-orange-50 p-3 rounded border-l-4 border-orange-400">
                <p className="text-sm font-medium text-orange-800 mb-1">
                  ⚠️ {language === 'fr' ? 'Phase de Stress Secondaire' : 'Secondary Stress Phase'}
                </p>
                <p className="text-sm text-orange-700">
                  {stressAnalysis.secondary.data.stressPhase[language]}
                </p>
              </div>
              
              <div className="bg-green-50 p-3 rounded border-l-4 border-green-400">
                <p className="text-sm font-medium text-green-800 mb-1">
                  💬 {language === 'fr' ? 'Canal Complémentaire' : 'Complementary Channel'}
                </p>
                <p className="text-sm text-green-700">
                  {stressAnalysis.secondary.data.channel[language]}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recommandations de coaching */}
      <div className="bg-gradient-to-br from-[#0f1c47]/5 to-[#e62c86]/5 p-6 rounded-lg border border-[#e62c86]/20">
        <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          🚀 {language === 'fr' ? 'Axes de Coaching Prioritaires' : 'Priority Coaching Areas'}
        </h4>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <h5 className="font-medium text-red-700 mb-2">
              {language === 'fr' ? '⚠️ Points d\'Attention (Zones de Stress)' : '⚠️ Watch Points (Stress Zones)'}
            </h5>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• {stressAnalysis.dominant?.data.stressPhase[language]}</li>
              {stressAnalysis.secondary && (
                <li>• {stressAnalysis.secondary.data.stressPhase[language]}</li>
              )}
              <li>• {language === 'fr' ? 'Signaux de surcharge mentale' : 'Mental overload signals'}</li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium text-green-700 mb-2">
              {language === 'fr' ? '💪 Leviers de Transformation' : '💪 Transformation Levers'}
            </h5>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• {language === 'fr' ? 'Adapter le style au contexte' : 'Adapt style to context'}</li>
              <li>• {language === 'fr' ? 'Développer la flexibilité comportementale' : 'Develop behavioral flexibility'}</li>
              <li>• {language === 'fr' ? 'Reconnaître les signaux d\'équipe' : 'Recognize team signals'}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PCMAnalysisChart;